/**
 * One of the four app-grid slots in the App Grid Control panel. Both a drop
 * target (drag a card here to move it into that position) and a drag source
 * (drag between slots to swap positions). Ported from the LifeScribe original.
 */

import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import StarIcon from '@mui/icons-material/Star';
import { colors } from '../../../theme/designTokens';
import { OrderItem } from './types';

export interface GridDropSlotProps {
  index: number;
  item: OrderItem | null;
  isMostPopular: boolean;
}

const GridDropSlot: React.FC<GridDropSlotProps> = ({ index, item, isMostPopular }) => {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `grid-slot-${index}` });
  const { setNodeRef: setDragRef, attributes, listeners, isDragging } = useDraggable({
    id: `grid-drag-${index}`,
    disabled: !item,
  });

  const mergedRef = useCallback(
    (node: HTMLElement | null) => {
      setDropRef(node);
      setDragRef(node);
    },
    [setDropRef, setDragRef]
  );

  const isEmpty = !item;

  return (
    <Box
      ref={mergedRef}
      {...(item ? { ...attributes, ...listeners } : {})}
      sx={{
        borderRadius: '8px',
        border: isOver
          ? `2px solid ${colors.warmGold[500]}`
          : isEmpty
            ? '1.5px dashed rgba(255,255,255,0.2)'
            : isMostPopular
              ? `1.5px solid ${colors.warmGold[500]}`
              : '1.5px solid rgba(255,255,255,0.18)',
        bgcolor: isOver
          ? 'rgba(212,172,13,0.12)'
          : isEmpty
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(255,255,255,0.07)',
        p: 1.2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 70,
        position: 'relative',
        transition: 'border-color 150ms ease, background-color 150ms ease, transform 150ms ease',
        transform: isOver ? 'scale(1.03)' : 'none',
        cursor: item ? 'grab' : 'default',
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {isMostPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: -1,
            left: -1,
            right: -1,
            bgcolor: colors.warmGold[500],
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.3,
            py: 0.2,
            borderRadius: '7px 7px 0 0',
            fontSize: '0.66rem',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            letterSpacing: '0.03em',
          }}
        >
          <StarIcon sx={{ fontSize: 8 }} />
          MOST POPULAR
        </Box>
      )}

      {isEmpty ? (
        <>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '0.72rem',
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 600,
              mt: isMostPopular ? 1 : 0,
            }}
          >
            Drop here
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '0.62rem',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            Position #{index + 1}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: '0.82rem',
              color: '#fff',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              mt: isMostPopular ? 1 : 0,
            }}
          >
            {item.name.length > 14 ? `${item.name.slice(0, 14)}...` : item.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '0.62rem',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            #{index + 1}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default GridDropSlot;
